import { useNavigate } from 'react-router-dom';
import type { PlannedEmployeeWeek, Location } from '@functieloonhuis/shared';
import { isSameDay } from '../utils/dateUtils';

interface PlanningMonthGridProps {
  planned: PlannedEmployeeWeek[];
  /** All Mon–Fri workday dates in the month, in order */
  workdays: Date[];
  filteredDay?: number | null;
  filteredLocation?: Location | null;
  showDepartment?: boolean;
  stickyHeader?: boolean;
}

/** Half-filled dot using conic-gradient — left = ochtend, right = middag */
function HalfDot({
  location,
  halfDayPeriod,
  title,
}: {
  location: 'kantoor' | 'thuis';
  halfDayPeriod: 'ochtend' | 'middag' | null | undefined;
  title: string;
}) {
  const workColor = location === 'kantoor' ? '#4ade80' : '#60a5fa';
  const freeColor = '#e2e8f0';

  // ochtend = left half worked, right free
  // middag  = left half free, right worked
  const gradient =
    halfDayPeriod === 'ochtend'
      ? `conic-gradient(${workColor} 180deg, ${freeColor} 180deg)`
      : `conic-gradient(${freeColor} 180deg, ${workColor} 180deg)`;

  return (
    <div
      className="w-7 h-7 rounded-full mx-auto"
      style={{ background: gradient }}
      title={title}
      role="img"
      aria-label={title}
    />
  );
}

function MonthDot({
  entry,
  employeeName,
  dimmed,
}: {
  entry: PlannedEmployeeWeek['days'][number];
  employeeName: string;
  dimmed?: boolean;
}) {
  const opacity = dimmed ? 'opacity-30' : '';

  if (entry.isHoliday) {
    return (
      <div
        className={`w-7 h-7 rounded-full bg-amber-400 mx-auto ${opacity}`}
        title={entry.holidayName ?? 'Feestdag'}
        role="img"
        aria-label={`${employeeName}, ${entry.holidayName ?? 'Feestdag'}`}
      />
    );
  }

  if (!entry.isWorkday) {
    return (
      <div
        className={`w-7 h-7 rounded-full bg-gray-200 mx-auto ${opacity}`}
        title="Vrij"
        role="img"
        aria-label={`${employeeName}, vrij`}
      />
    );
  }

  const location = entry.location as 'kantoor' | 'thuis';
  const label = `${employeeName}, ${location}${entry.isHalfDay ? ` (${entry.halfDayPeriod ?? 'halve dag'})` : ''}`;

  if (entry.isHalfDay) {
    return (
      <div className={opacity}>
        <HalfDot
          location={location}
          halfDayPeriod={entry.halfDayPeriod}
          title={label}
        />
      </div>
    );
  }

  const dotColor = location === 'kantoor' ? 'bg-green-400' : 'bg-blue-400';

  return (
    <div
      className={`w-7 h-7 rounded-full mx-auto ${dotColor} ${opacity}`}
      title={label}
      role="img"
      aria-label={label}
    />
  );
}

export function PlanningMonthGrid({
  planned,
  workdays,
  filteredDay,
  filteredLocation,
  showDepartment,
  stickyHeader,
}: PlanningMonthGridProps) {
  const navigate = useNavigate();
  const today = new Date();
  const hasFilters = !!filteredDay || !!filteredLocation;

  return (
    <div className="overflow-x-auto w-full">
      <table className="w-full border-collapse" role="grid" aria-label="Maandplanning">
        <thead className={stickyHeader ? 'sticky top-0 z-10 bg-white' : ''}>
          <tr role="row">
            <th className="text-left text-sm font-medium text-gray-500 py-2.5 px-3 w-48 sticky left-0 bg-white" role="columnheader">
              Medewerker
            </th>
            {showDepartment && (
              <th className="text-left text-sm font-medium text-gray-500 py-2.5 px-3 w-36 sticky left-48 bg-white" role="columnheader">
                Team
              </th>
            )}
            {workdays.map(date => {
              const dow = date.getDay() === 0 ? 7 : date.getDay();
              const isToday = isSameDay(date, today);
              const isHighlighted = filteredDay === dow;
              return (
                <th
                  key={date.toISOString()}
                  className={`text-center py-2.5 px-1 ${
                    isToday
                      ? 'text-blue-700 bg-blue-50'
                      : isHighlighted
                        ? 'text-indigo-700 bg-indigo-50'
                        : 'text-gray-500'
                  } ${hasFilters && !isHighlighted && !isToday && filteredDay ? 'opacity-40' : ''}`}
                  role="columnheader"
                >
                  <div className="text-xs font-semibold">{date.getDate()}</div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {planned.map(row => (
            <tr key={row.employee.id} role="row" className="hover:bg-gray-50">
              <td className="py-2 px-3 sticky left-0 bg-white">
                <button
                  onClick={() => navigate(`/medewerkers/${row.employee.id}?tab=werkrooster`)}
                  className="flex items-center gap-2 text-left hover:text-blue-600 transition-colors"
                >
                  <div className="h-7 w-7 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-600 font-medium text-xs">
                      {row.employee.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="text-sm font-medium text-gray-900 truncate max-w-[140px]">
                    {row.employee.name}
                  </span>
                </button>
              </td>
              {showDepartment && (
                <td className="py-2 px-3 sticky left-48 bg-white">
                  <span className="text-sm text-gray-500 truncate">{row.employee.departmentName || '—'}</span>
                </td>
              )}
              {workdays.map(date => {
                const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                const day = row.days.find(d => d.date === dateStr);
                const dow = date.getDay() === 0 ? 7 : date.getDay();
                const isToday = isSameDay(date, today);

                let dimmed = false;
                if (hasFilters) {
                  if (filteredDay && dow !== filteredDay) dimmed = true;
                  if (filteredLocation && day?.location !== filteredLocation) dimmed = true;
                }

                return (
                  <td
                    key={dateStr}
                    className={`py-2 px-1 text-center ${isToday ? 'bg-blue-50' : ''}`}
                    role="gridcell"
                  >
                    {day ? (
                      <MonthDot
                        entry={day}
                        employeeName={row.employee.name}
                        dimmed={dimmed}
                      />
                    ) : (
                      <div className="w-7 h-7 mx-auto" />
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
